import { KindEnum } from 'wflow-variables';
import { ERP_TYPE } from 'wflow-main-app/src/app/shared/__const/erp-types.const';

/**
 * MatrixKind groups document kinds that share the same ERP-field defaults.
 * SupplierOrder/CustomerOrder follow IncomingInvoice;
 * IncomeCashReceipt follows ExpenditureCashSlip; Other gets all fields ON.
 */
export type MatrixKind = 'incomingInvoice' | 'outgoingInvoice' | 'cashReceipt';

const KIND_TO_MATRIX: Partial<Record<KindEnum, MatrixKind | 'all'>> = {
  [KindEnum.IncomingInvoice]: 'incomingInvoice',
  [KindEnum.OutgoingInvoice]: 'outgoingInvoice',
  [KindEnum.ExpenditureCashSlip]: 'cashReceipt',
  [KindEnum.IncomeCashReceipt]: 'cashReceipt',
  [KindEnum.SupplierOrder]: 'incomingInvoice',
  [KindEnum.CustomerOrder]: 'incomingInvoice',
  [KindEnum.Other]: 'all',
};

type ErpFieldDefaults = { default: readonly string[] } & Partial<
  Record<MatrixKind, readonly string[]>
>;

export const ERP_FIELD_DEFAULTS: Partial<Record<ERP_TYPE, ErpFieldDefaults>> = {
  [ERP_TYPE.ABRA_FLORES]: {
    default: [
      'accountingRule',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
    ],
    outgoingInvoice: [],
  },
  [ERP_TYPE.ABRA_GEN]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.BUSINESS_CENTRAL]: {
    default: [
      'activity',
      'businessCase',
      'businessItemCode',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatControlStatementLine',
      'vatMode',
      'vehicle',
    ],
    cashReceipt: [
      'activity',
      'businessCase',
      'businessItemCode',
      'cashRegister',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatControlStatementLine',
      'vatMode',
      'vehicle',
    ],
  },
  [ERP_TYPE.DATEV]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.DUEL]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.EKONOM]: {
    default: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.ENTRY]: {
    default: [
      'accountingRule',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
    cashReceipt: [
      'accountingRule',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.ESO9]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.FLEXIBEE]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
    cashReceipt: [
      'activity',
      'businessItemCode',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'project',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.HARMONIK]: {
    default: ['accountingRule', 'activity', 'paymentMethod', 'vatMode'],
    outgoingInvoice: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.HELIOS]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'vatApplyReductionCoefficient',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
    outgoingInvoice: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'cardDocumentType',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'vatApplyReductionCoefficient',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.IDOKLAD]: {
    default: [
      'businessItemCode',
      'cashRegister',
      'paymentMethod',
      'vatApplyReductionCoefficient',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatSupplyModeCode',
    ],
  },
  [ERP_TYPE.K2]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatMode',
      'vatReturnLine',
      'vehicle',
    ],
  },
  [ERP_TYPE.K6]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.KELOC]: {
    default: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.KROS]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.MONEYS3]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.MONEYS4]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.MRP]: {
    default: [
      'accountingRule',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
    cashReceipt: [
      'accountingRule',
      'cashRegister',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.OMEGA_KROS]: {
    default: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.POHODA]: {
    default: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'businessItemCode',
      'cashRegister',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
    ],
  },
  [ERP_TYPE.PREMIER]: {
    default: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReverseChargeCode',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatControlStatementLine',
      'vatMode',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.STEREO_KASTNER]: {
    default: [
      'accountingRule',
      'activity',
      'contraAccount',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.UOL]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
  [ERP_TYPE.VARIO]: {
    default: [
      'accountingRule',
      'activity',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
    cashReceipt: [
      'accountingRule',
      'activity',
      'cashRegister',
      'contract',
      'costCenter',
      'paymentMethod',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
    ],
  },
  [ERP_TYPE.XERO]: {
    default: [
      'accountingRule',
      'activity',
      'businessCase',
      'businessItemCode',
      'cardDocumentType',
      'cashDocumentType',
      'cashRegister',
      'contraAccount',
      'contract',
      'costCenter',
      'employee',
      'paymentMethod',
      'project',
      'vatApplyReductionCoefficient',
      'vatControlStatementLine',
      'vatFixedAssets',
      'vatMode',
      'vatReturnLine',
      'vatReverseChargeCode',
      'vatSupplyModeCode',
      'vehicle',
    ],
  },
};

export const erpFieldsByCountryVat = new Map<string, Map<string, ERP_TYPE[]>>([
  ['SK', new Map([['vatControlStatementLine', [ERP_TYPE.POHODA]]])],
]);

/**
 * Whether the matrix covers this kind. A kind with no entry (Contract) resolves
 * to "every field disabled", which is indistinguishable from a deliberate all-off
 * configuration — so a caller seeding defaults has to be able to skip it rather
 * than write it.
 */
export const hasKindFieldDefaults = (kind: KindEnum): boolean =>
  KIND_TO_MATRIX[kind] != null;

/**
 * Whether the matrix covers this accounting system. False for `other`,
 * `undefined` and `none` — none of which has a field set of its own, though only
 * `none` carries an instruction ("turn everything off") that a caller may want to
 * act on separately.
 */
export const hasErpFieldDefaults = (erpCode: string): boolean =>
  ERP_FIELD_DEFAULTS[erpCode as ERP_TYPE] != null;

export const isFieldEnabledForErpAndKind = (
  registerName: string,
  erpCode: string,
  kind: KindEnum,
  vatCountry = 'CZ',
): boolean => {
  const matrixKind = KIND_TO_MATRIX[kind];

  if (matrixKind === 'all') {
    return true;
  }
  if (!matrixKind) {
    return false;
  }

  const erpDefaults = ERP_FIELD_DEFAULTS[erpCode as ERP_TYPE];
  const enabledFields = erpDefaults?.[matrixKind] ?? erpDefaults?.default ?? [];
  if (enabledFields.includes(registerName)) {
    return true;
  }

  const countrySpecific =
    erpFieldsByCountryVat.get(vatCountry)?.get(registerName) ?? [];
  return countrySpecific.includes(erpCode as ERP_TYPE);
};
